import React, { useState } from "react"
import PropTypes from "prop-types"
import BioModal from "./bioModal"
import "./clinicTeam.scss"

// Cards cycle through the brand pastels, matching the services panels.
const TONES = ["pink", "blue", "yellow", "green"]

const Chevron = () => (
  <svg
    aria-hidden="true"
    className="ClinicTeam__chevron"
    fill="none"
    viewBox="0 0 24 24"
  >
    <path
      d="m9 6 6 6-6 6"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    />
  </svg>
)

// "Carlsbad, CA" -> "Carlsbad", so the heading reads the way the design does.
const cityOf = name => (name || "").split(",")[0].trim()

const EMAIL = /[\w.+-]+@[\w.-]+\.\w+/
const PHONE = /\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}[^,]*/

/**
 * Contact details come from the Email and Phone fields on Staff Member.
 *
 * Before those fields existed, editors typed them into the role instead, e.g.
 * "Site Director, bsiegan@corticacare.com, (619) 377-6451". Those entries are
 * still read, and the role is cleaned up for display, so nothing breaks while
 * they are moved across. Retire the fallback once no entry does that.
 */
const parseContact = doctor => {
  const raw = doctor.role || ""
  const email = (raw.match(EMAIL) || [])[0]
  const phone = (raw.match(PHONE) || [])[0]

  const role = raw
    .split(",")
    .map(part => part.trim())
    .filter(part => part && !EMAIL.test(part) && !PHONE.test(part))
    .join(", ")

  return {
    ...doctor,
    email: doctor.email || email,
    phone: doctor.phone || phone,
    role: role || raw,
  }
}

const ClinicTeam = ({ clinic, doctors, title }) => {
  const [openIndex, setOpenIndex] = useState(null)

  if (!doctors?.length) return null

  const city = cityOf(clinic?.name)
  const heading = city ? `The ${city} Autism Therapy Team` : title
  const people = doctors.map(parseContact)
  const open = openIndex === null ? null : people[openIndex]

  return (
    <section className="ClinicTeam">
      <div className="container-v2">
        <h2 className="ClinicTeam__title">{heading}</h2>

        <ul className="ClinicTeam__grid">
          {people.map((doctor, index) => {
            // Only offer the dialog when there is something to read.
            const hasBio = Boolean(doctor.details?.json)
            const tone = TONES[index % TONES.length]

            return (
              <li
                className={`ClinicTeam__card ClinicTeam__card--${tone}`}
                key={doctor.slug || doctor.name}
              >
                {doctor.picture?.fluid?.src && (
                  <img
                    alt={doctor.name}
                    className="ClinicTeam__photo"
                    src={doctor.picture.fluid.src}
                  />
                )}

                <p className="ClinicTeam__name">{doctor.name}</p>
                {doctor.role && (
                  <p className="ClinicTeam__role">{doctor.role}</p>
                )}

                {hasBio && (
                  <button
                    aria-haspopup="dialog"
                    className="ClinicTeam__more"
                    onClick={() => setOpenIndex(index)}
                    type="button"
                  >
                    See More
                    <Chevron />
                  </button>
                )}
              </li>
            )
          })}
        </ul>
      </div>

      {open && (
        <BioModal
          doctor={open}
          onClose={() => setOpenIndex(null)}
          tone={TONES[openIndex % TONES.length]}
        />
      )}
    </section>
  )
}

ClinicTeam.propTypes = {
  clinic: PropTypes.shape({ name: PropTypes.string }),
  doctors: PropTypes.arrayOf(
    PropTypes.shape({
      details: PropTypes.shape({ json: PropTypes.object }),
      name: PropTypes.string,
      picture: PropTypes.shape({
        fluid: PropTypes.shape({ src: PropTypes.string }),
      }),
      role: PropTypes.string,
      slug: PropTypes.string,
    })
  ),
  title: PropTypes.string,
}

export default ClinicTeam
